import React, { useState, useRef, useEffect } from 'react';
import { LobbyMessage, Profile } from '@/types/supabase';
import { UserConnection } from '@/hooks/useUserMessages';
import { useAuth } from '@/contexts/AuthContext';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Bot } from 'lucide-react';
import { formatLobbyTimestamp } from '@/lib/utils';
import ChatMessageContent from './ChatMessageContent';
import MessageReactions from './MessageReactions';
import EmojiPickerPopover from './EmojiPickerPopover';
import MessageActions from './MessageActions';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useMessagingStore } from '@/store/useMessagingStore';
import { EmojiClickData } from 'emoji-picker-react'; // Import EmojiClickData

interface LobbyMessageItemProps {
  message: LobbyMessage;
  profile: Profile | null;
  connections: UserConnection[];
  onEdit: (messageId: string, content: string) => Promise<void>;
  onDelete: (messageId: string) => Promise<void>;
  onToggleReaction: (messageId: string, emojiData: EmojiClickData) => void; // Changed type
  onConnect: (userId: string, userName: string) => void;
  channelMembers: Profile[];
}

const LobbyMessageItem: React.FC<LobbyMessageItemProps> = ({ message, profile, connections, onEdit, onDelete, onToggleReaction, onConnect, channelMembers }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { openMessaging } = useMessagingStore();
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(message.content);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [isEditing]);

  const handleSave = async () => {
    const newContent = editedContent.trim();
    if (newContent && newContent !== message.content) {
      await onEdit(message.id, newContent);
    } else if (!newContent) {
      toast({ title: "Message cannot be empty", variant: "destructive" });
      return;
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditedContent(message.content);
    setIsEditing(false);
  };

  const handleTextareaKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    }
    if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const canEdit = message.user_id === user?.id;
  const canDelete = profile?.role === 'admin' || message.user_id === user?.id;
  const isOwnMessage = message.user_id === user?.id;

  const existingConnection = connections.find(
    c => (c.user_id === message.user_id && c.connected_user_id === user?.id) || (c.user_id === user?.id && c.connected_user_id === message.user_id)
  );

  const canConnect = !isOwnMessage && (!existingConnection || existingConnection.status === 'declined');
  const canMessage = !isOwnMessage && existingConnection?.status === 'accepted';

  const handleMessage = () => {
    if (message.user_id && message.profiles?.full_name) {
      openMessaging({ id: message.user_id, name: message.profiles.full_name });
    }
  };

  return (
    <div className="group relative flex items-start gap-4 p-2 rounded-md hover:bg-muted/50">
      <Avatar className="h-10 w-10">
        <AvatarImage src={message.profiles?.avatar_url || undefined} />
        <AvatarFallback>
          {message.profiles?.full_name?.charAt(0) || 'U'}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1">
        <div className="flex items-baseline gap-2">
          <p className="font-semibold">{message.profiles?.full_name || 'User'}</p>
          <p className="text-xs text-muted-foreground">
            {formatLobbyTimestamp(message.created_at)}
          </p>
        </div>
        {isEditing ? (
          <div className="mt-1 space-y-2">
            <Textarea
              ref={textareaRef}
              value={editedContent}
              onChange={(e) => setEditedContent(e.target.value)}
              onKeyDown={handleTextareaKeyDown}
              className="text-sm resize-none"
              rows={1}
            />
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={handleSave}>Save</Button>
              <Button size="sm" variant="ghost" onClick={handleCancel}>Cancel</Button>
            </div>
          </div>
        ) : (
          <>
            <ChatMessageContent content={message.content} channelMembers={channelMembers} />
            {message.edited_at && (
              <span className="text-xs text-muted-foreground ml-2">(edited)</span>
            )}
            <MessageReactions
              message={message}
              onToggleReaction={(emoji) => onToggleReaction(message.id, { emoji, imageUrl: message.reactions?.[emoji]?.imageUrl || '' } as EmojiClickData)}
            />
          </>
        )}
      </div>
      {!isEditing && (
        <div className="absolute top-1 right-1 flex items-center gap-2 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
          <MessageActions
            onEdit={() => setIsEditing(true)}
            onDelete={() => onDelete(message.id)}
            onConnect={() => onConnect(message.user_id, message.profiles?.full_name || 'User')}
            onMessage={handleMessage}
            canEdit={canEdit}
            canDelete={canDelete}
            canConnect={canConnect}
            canMessage={canMessage}
          />
          <EmojiPickerPopover
            onEmojiSelect={(emojiData) => onToggleReaction(message.id, emojiData)}
          />
        </div>
      )}
    </div>
  );
};

export default LobbyMessageItem;